import type { Contact } from 'features/contacts/types'
import type { ContactQualification } from 'features/executions/types'

import { executionsApi } from 'features/executions'
import { notificationUtils } from 'features/notifications'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch } from 'shared/hooks'

import type { AddToSystemProps } from '../AddToSystem/AddToSystem'

import { api } from '../../api'

export interface SelectProps extends AddToSystemProps {
	contactId: string
}

type PatchQualification = Pick<Contact, 'id' | 'qualification'>

export function useSelectLogic({
	contactId,
	entityId
}: { contactId: string; entityId?: string }) {
	const { conversation } = useParams<{ conversation?: string }>()
	const optimisticUpdate = useOptimisticUpdate()

	const {
		data: execution,
		isLoading,
		isSuccess
	} = executionsApi.useMostRelevantExecutionQuery(
		{
			contact_entity_id: entityId ?? '',
			conversation_id: conversation ?? ''
		},
		{
			skip: !(entityId && conversation)
		}
	)

	const { data: contact } = api.useContactByIdQuery(contactId, {
		skip: !contactId
	})
	const [changeContact, contactOptions] =
		api.useChangeContactQualificationMutation()

	const [changeExecution, executionOptions] =
		executionsApi.useChangeExecutionQualificationMutation()

	async function update(nextValue: ContactQualification) {
		const undo = optimisticUpdate({
			id: contactId,
			qualification: nextValue
		})

		let data: object | undefined = undefined

		if (typeof execution === 'string') {
			const response = await changeContact({
				id: contactId,
				qualification: nextValue
			})
			data = response.data
		}

		if (typeof execution === 'object') {
			const response = await changeExecution({
				id: execution.id,
				qualification: nextValue
			})
			data = response.data
		}

		if (data) {
			notificationUtils.snackById('n3.36')
		} else {
			undo()
		}
	}

	const isInProgress =
		isLoading || contactOptions.isLoading || executionOptions.isLoading

	return {
		current: contact?.qualification,
		isInProgress,
		isReady: isSuccess,
		update
	}
}

function useOptimisticUpdate() {
	const dispatch = useAppDispatch()

	return useCallback(
		(patch: PatchQualification) => {
			const actionById = api.util.updateQueryData(
				'contactById',
				patch.id,
				draft => Object.assign(draft, patch)
			)

			const updatedById = dispatch(actionById)

			return () => {
				updatedById.undo()
			}
		},
		[dispatch]
	)
}
